import React from 'react';
import PropTypes from 'prop-types';
import { Container } from '@mui/material';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';


/**
 * Simple tabber component wrapper around MUI Tabs.
 * @param {Object} props - The props for the component.
 * @param {Array<Object>} props.files - Array of file objects.
 * @param {Function} props.onDownloadRequest - Function for handling file download click requests.
 * @param {Array<String>} props.actions - Array of actions, defaults to none.
 * @param {Array<String>} props.widthHeight - Pixel value for width/height of images. Defaults to 164.
 * @component
 * @example
  const fileInfo = [{
    1,
    'Jack_Dangermond_Selfie.jpg',
    thumbnailUrl: 'https://via.placeholder.com/150/0000FF/808080%20?Text=Digital.com'
  }]
  const handleChange = (value) => { console.log(value) }
  return (
    <FileViewer
      files={fileInfo}
      onDownloadRequest={handleChange}
    />
  )
 * @see https://mui.com/material-ui/react-tabs/
 */
const FileViewer = ({ files, onDownloadRequest, actions = [], widthHeight = 164 }) => {
  // Refs for the subheader native element
  const subHeaderRef = React.useRef();
  // Tracks state for the subheader font size
  const [subHeaderFontSize, setSubHeaderFontSize] = React.useState(16);

  // Get subheader font size when component mounts
  React.useEffect(() => {
    window.addEventListener('DOMContentLoaded', (_event) => {
      const size = window
        .getComputedStyle(subHeaderRef.current, null)
        .getPropertyValue('font-size');

      setSubHeaderFontSize(size);
    });
  }, []);

  // Handle actions - A bit redundant now as we are no longer using Chonky UI
  // Could be a good pattern to leverage though if we need to add additional actions
  const handleAction = (data) => {
    console.log('File action data:', data);

    if (data.id === 'open-files') {
      console.log('open files');
      // Unpack id using map from data.payload.files
      const ids = data?.payload?.files?.map((file) => file.id) || [];
      onDownloadRequest(ids);
    }
  };

  // Render the file/image cards for each file in files.  Has a fallback to a image in static
  // folder if the image fails to load.
  const renderCards = () => {
    return files.map((file) => {
      return (
        <React.Fragment key={file.id}>
          {/* Tooltip wrapper - sometimes the file name gets too long and will be truncated in subheader  */}
          <Tooltip title={file.name}>
            {/* Image card with on click action */}
            <ImageListItem
              sx={{ padding: '1rem', cursor: 'pointer' }}
              onClick={() => handleAction({ id: 'open-files', payload: { files: [file] } })}
            >
              {/* Image with fallback URL for non-images (ex: PDF) */}
              <img
                src={getUrl(file)}
                srcSet={getSrcSet(file)}
                alt={file.name}
                loading="lazy"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // Prevents looping
                  currentTarget.src = '/file_fallback.png';
                }}
                style={{borderRadius: '0.5rem'}}
              />
              {/* Sub header with file name */}
              <Typography
                variant="sectionHeader"
                ref={subHeaderRef}
                sx={{ textAlign: 'center', maxWidth: widthHeight }}
              >{truncateFileName(file.name)}</Typography>
            </ImageListItem>
          </Tooltip>
        </React.Fragment>
      );
    });
  };

  // Render function when there are no cards to render
  const renderNoCards = () => {
    return (
      <Typography variant="sectionHeader" sx={{ textAlign: 'center' }}>
        There are no Photos or Attachments related to this Activity. Photos and Attachments can be added to this Activity in Survey123.
      </Typography>
    );
  };

  // Helper function to get the image url for the image by locking its width and height
  // We assume that the URL from the backend already has the "?" and query params so we
  // are just adding to it with "&".
  const getUrl = (file) => {
    return `${file.thumbnailUrl}&w=${widthHeight}&h=${widthHeight}&fit=crop&auto=format`;
  };

  // Helper function to get the source set for the image by locking its width and height
  // We assume that the URL from the backend already has the "?" and query params so we
  // are just adding to it with "&".
  const getSrcSet = (file) => {
    return `${file.thumbnailUrl}&w=${widthHeight}&h=${widthHeight}&fit=crop&auto=format&dpr=2 2x`;
  };

  // Helper function to truncate the file name if it is longer than the widthHeight with "..."
  const truncateFileName = (fileName) => {
    // File name length * font size in pixels
    const estimatedNamePx = fileName.length * subHeaderFontSize;

    // Width of the image container in pixels / font size in pixels
    const estimatedBoxPx = widthHeight / subHeaderFontSize;

    // If the estimated name length is longer than the estimated box length, truncate the name
    if (estimatedNamePx > estimatedBoxPx) {
      return `${fileName.substring(0, (estimatedBoxPx + 6))}...`; // the +6 gives a couple characters of leeway
    }

    return fileName;
  };

  // 🌎 Main container for the FileViewer
  return (
    <Container sx={{ minHeight: 300 }}>
      {files?.length > 0 && <ImageList sx={{ width: '100%', height: 450 }} cols={5} rowHeight={widthHeight}>
        {renderCards()}
      </ImageList>}
      {(files?.length == 0 || !files) && renderNoCards()}
    </Container >
  );
};

FileViewer.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    thumbnailUrl: PropTypes.string,
  })),
  onDownloadRequest: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.string),
  widthHeight: PropTypes.number,
};

export default FileViewer;
